import React, { useState } from "react";

import { makePostApiCall, makeGetApiCall } from "../../../shared/makeApiCall";
import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import { SnackbarData, SnackbarType } from "../../../components/Snackbar";
import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";
import ProductRndEquipmentForm from "../../../components/forms/productRndEquipment/productRndEquipmentForm";

export interface ProductRndEquipment {
  id: number;
  product_id: number;
  equipment_name: string;
  description?: string | null;
  equipment_barcode?: string | null;
  required_maintenance_date?: string | null;
  image1?: string | null;
  image2?: string | null;
}

export function EquipmentViewModel(
  logout: () => void,
  productId: number,
  addSnackbar?: any
) {
  const [equipmentOptions, setEquipmentOptions] = useState<any[]>([]);
  const [assignedEquipments, setAssignedEquipments] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);
  const [emptyModal, setEmptyModelOpen] = useState<{
    open: boolean;
    childComponent: React.ReactNode | null;
  }>({
    open: false,
    childComponent: null,
  });

  const [slideOver, setSlideOver] = useState<{
    childComponent: React.ReactNode | null;
    open: boolean;
  }>({
    childComponent: null,
    open: false,
  });

  const [equipments, setEquipments] = useState<any>({
    data: [],
    total: 0,
  });

  const [equipmentForm, setEquipmentForm] = useState({
    name: "",
    description: "",
  });

  const getEquipments = async function (search: string | null, page: number) {
    setLoading(true);

    const payload = {
      page: page,
      search: search,
    };
    const callResult: any = await makePostApiCall(`equipment`, payload);
    setLoading(false);
    if (callResult.success) {
      setEquipments((callResult.data.equipment as ProductRndEquipment[]) || []);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal(
          "Error fetching equipments",
          callResult.data?.message || "Please try again later."
        );
      }
    }
  };

  const createEquipment = async function (values: any) {
    const payload = { ...values };
    const callResult: any = await makePostApiCall(
      "createEquipment",
      payload,
      "multipart/form-data"
    );
    if (callResult.success) {
      setSlideOver({ childComponent: null, open: false });
      getEquipments(null, 1);
      const id = `${Date.now()}`;
      addSnackbar(
        new SnackbarData(id, "Equipment Added", SnackbarType.Success)
      );
    } else {
      showErrorModal(
        "Error creating equipment",
        callResult.data?.message || "Please try again later."
      );
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const updateEquipment = async function (
    equipment: ProductRndEquipment,
    values: any
  ) {
    const callResult: any = await makePostApiCall(
      `updateEquipment/${equipment.id}`,
      values,
      "multipart/form-data"
    );
    if (callResult.success) {
      setSlideOver({ childComponent: null, open: false });
      getEquipments(null, 1);
      const id = `${Date.now()}`;
      addSnackbar(
        new SnackbarData(id, "Equipment updated", SnackbarType.Success)
      );
    } else {
      showErrorModal(
        "Error updating equipment",
        callResult.data?.message || "Please try again later."
      );
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const deleteEquipment = async function (id: number) {
    setLoading(true);
    const callResult: any = await makePostApiCall(`deleteEquipment/${id}`, {});
    setLoading(false);
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      getEquipments(null, 1);

      const sid = `${Date.now()}`;
      addSnackbar(
        new SnackbarData(sid, "Equipment deleted", SnackbarType.Error)
      );
    } else {
      showErrorModal(
        "Error deleting equipment",
        callResult.data?.message || "Please try again later."
      );
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const onChangeEquipmentField = (field: string, value: string) => {
    setEquipmentForm((s) => ({ ...s, [field]: value }));
  };

  const addEquipment = () => {
    if (!equipmentForm.name.trim()) return;
    const newItem = {
      product_id: productId || 0,
      equipment_name: equipmentForm.name.trim(),
      description: equipmentForm.description.trim(),
    };
    createEquipment(newItem);

    getEquipments(null, 1);
    setEquipmentForm({ name: "", description: "" });
  };

  const removeEquipment = (id: number | string) => {
    deleteEquipment(Number(id));
  };

  const askDeleteEquipment = function (equipment: ProductRndEquipment) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          message={`Are you sure you want to delete ${equipment.equipment_name}?`}
          confirmAction={() => deleteEquipment(equipment.id)}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const openAddEquipmentSlideOver = (productId?: number) => {
    setSlideOver({
      childComponent: (
        <ProductRndEquipmentForm
          isEdit={false}
          productId={productId}
          setOpen={setSlideOver}
          actionCall={createEquipment}
        />
      ),
      open: true,
    });
  };

  const viewEquipmentDetails = (equipment: ProductRndEquipment) => {
    setSlideOver({
      childComponent: (
        <ProductRndEquipmentForm
          isEdit={true}
          equipment={equipment}
          setOpen={setSlideOver}
          actionCall={updateEquipment}
        />
      ),
      open: true,
    });
  };

  const showErrorModal = (title: string, subtitle?: string) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const assignEquipmentToProduct = async function (
    product_id: number,
    equipment_id: number
  ) {
    let payload = {
      equipment_id: equipment_id,
      product_id: product_id,
    };

    const callResult: any = await makePostApiCall(
      `assignEquipmentToProduct`,
      payload
    );

    if (callResult.success) {
      const id = `${Date.now()}`;
      addSnackbar(
        new SnackbarData(
          id,
          "Equipment assigned to product",
          SnackbarType.Success
        )
      );
      return true;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      return false;
    }
  };

  const fetchAssignedEquipmentToProduct = async function (product_id: number) {
    const callResult: any = await makeGetApiCall(
      `assignedEquipments/${product_id}`
    );

    if (callResult.success) {
      const assigned = callResult.data || [];
      setAssignedEquipments(assigned);
      return assigned;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      return [];
    }
  };

  const unassignEquipmentFromProduct = async function (
    product_id: number,
    equipment_id: number
  ) {
    let payload = {
      equipment_id: equipment_id,
      product_id: product_id,
    };

    const callResult: any = await makePostApiCall(
      `unassignEquipmentFromProduct`,
      payload
    );
    if (callResult.success) {
      const id = `${Date.now()}`;
      addSnackbar(
        new SnackbarData(
          id,
          "Equipment unassigned from product",
          SnackbarType.Success
        )
      );
      return true;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      return false;
    }
  };

  const handleEquipmentChange = async function (newValue: any) {
    const newSelection = newValue || [];
    const previousSelection = assignedEquipments;

    // Find newly added equipment
    const added = newSelection.filter(
      (item: any) =>
        !previousSelection.find((prev: any) => prev.value === item.value)
    );

    // Find removed equipment
    const removed = previousSelection.filter(
      (item: any) =>
        !newSelection.find((newItem: any) => newItem.value === item.value)
    );

    // Optimistically update UI
    setAssignedEquipments(newSelection);

    // Process additions
    for (const item of added) {
      const success = await assignEquipmentToProduct(productId, item.value);
      if (!success) {
        // Revert on failure
        setAssignedEquipments(previousSelection);
        return;
      }
    }

    // Process removals
    for (const item of removed) {
      const success = await unassignEquipmentFromProduct(productId, item.value);
      if (!success) {
        // Revert on failure
        setAssignedEquipments(previousSelection);
        return;
      }
    }
  };

  const fetchEquipmentsForDropdown = async function () {
    const callResult: any = await makeGetApiCall(`equipmentForDropdown`);
    if (callResult.success) {
      setEquipmentOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  return {
    getEquipments,
    createEquipment,
    updateEquipment,
    deleteEquipment,
    loading,
    equipments,
    removeEquipment,
    addEquipment,
    equipmentForm,
    onChangeEquipmentField,
    emptyModal,
    setEmptyModelOpen,
    slideOver,
    setSlideOver,
    askDeleteEquipment,
    openAddEquipmentSlideOver,
    viewEquipmentDetails,
    assignEquipmentToProduct,
    unassignEquipmentFromProduct,
    fetchAssignedEquipmentToProduct,
    assignedEquipments,
    handleEquipmentChange,
    fetchEquipmentsForDropdown,
    equipmentOptions,
  };
}

export default EquipmentViewModel;
